import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { FaUserShield, FaArrowLeft, FaCar, FaEnvelope, FaLock, FaEye, FaEyeSlash, FaUser, FaSignInAlt } from 'react-icons/fa'
import { supabase } from '../supabaseClient'
import '../styles/styles.css'

export default function Signup() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [errorMsg, setErrorMsg] = useState('')
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isValidInvitation, setIsValidInvitation] = useState(false)
  const [checkingInvitation, setCheckingInvitation] = useState(true)
  const [passwordError, setPasswordError] = useState('')
  const [confirmPasswordError, setConfirmPasswordError] = useState('')

  // Obtener el token de invitación de la URL
  const invitationToken = searchParams.get('token')
  const invitationEmail = searchParams.get('email')

  // Validación en tiempo real de contraseñas
  useEffect(() => {
    if (password && confirmPassword) {
      if (password !== confirmPassword) {
        setConfirmPasswordError('Las contraseñas no coinciden')
      } else {
        setConfirmPasswordError('')
      }
    } else {
      setConfirmPasswordError('')
    }
  }, [password, confirmPassword])

  useEffect(() => {
    if (password && password.length < 6) {
      setPasswordError('La contraseña debe tener al menos 6 caracteres')
    } else {
      setPasswordError('')
    }
  }, [password])

  useEffect(() => {
    const validateInvitation = () => {
      if (!invitationToken) {
        setErrorMsg('Token de invitación no válido')
        setCheckingInvitation(false)
        return
      }

      // Si hay un token de invitación, asumimos que es válido
      // La validación real se hará durante el proceso de signup
      setIsValidInvitation(true)
      if (invitationEmail) {
        setEmail(invitationEmail)
      }
      setCheckingInvitation(false)
    }

    validateInvitation()
  }, [invitationToken, invitationEmail])

  const handleSignup = async (e) => {
    e.preventDefault()
    setErrorMsg('')

    // Validaciones
    if (password !== confirmPassword) {
      setErrorMsg('Las contraseñas no coinciden')
      return
    }

    if (password.length < 6) {
      setErrorMsg('La contraseña debe tener al menos 6 caracteres')
      return
    }

    if (!firstName.trim() || !lastName.trim()) {
      setErrorMsg('Por favor completa todos los campos')
      return
    }

    setLoading(true)

    try {
      console.log('🔄 Creando usuario:', { email, firstName, lastName })
      
      // Crear el usuario directamente con Supabase
      const { data, error } = await supabase.auth.signUp({
        email: email,
        password: password,
        options: {
          data: {
            first_name: firstName,
            last_name: lastName
          }
        }
      })

      if (error) {
        console.error('❌ Error al crear usuario:', error)
        setErrorMsg(error.message)
        setLoading(false)
        return
      }

      // Si el signup es exitoso, redirigir al dashboard
      if (data.user) {
        console.log('✅ Usuario creado exitosamente:', data.user)
        console.log('📝 Datos del usuario:', data.user.user_metadata)
        
        // Verificar que los datos se guardaron correctamente
        const { data: userData, error: userError } = await supabase.auth.getUser()
        if (userError) {
          console.error('❌ Error al obtener datos del usuario:', userError)
        } else {
          console.log('👤 Datos del usuario verificados:', userData.user)
        }
        
        // Redirigir al dashboard del empleado
        console.log('✅ Redirigiendo al dashboard')
        navigate('/empleado')
      } else {
        console.error('❌ No se recibieron datos del usuario')
        setErrorMsg('Error al crear la cuenta. Por favor intenta nuevamente.')
      }
    } catch (error) {
      console.error('❌ Error inesperado:', error)
      setErrorMsg('Error al crear la cuenta. Verifica que el link de invitación sea válido.')
    } finally {
      setLoading(false)
    }
  }

  const handleBack = () => {
    navigate('/')
  }

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword)
  }

  if (checkingInvitation) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="loading-spinner"></div>
          <p>Validando invitación...</p>
        </div>
      </div>
    )
  }

  if (!isValidInvitation) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <button onClick={handleBack} className="back-button">
              <FaArrowLeft />
              Volver
            </button>
          </div>

          <div className="login-logo">
            <div className="logo-icon">
              <FaCar />
            </div>
            <h1>LUBRICENTROS</h1>
          </div>

          <div className="login-title-section">
            <div className="user-icon">
              <FaUserShield />
            </div>
            <h2>Invitación Inválida</h2>
            <p>{errorMsg}</p>
          </div>

          <div className="login-footer">
            <p>Contacta al administrador para obtener una nueva invitación</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="login-container">
      <div className="login-card">
        {/* Header */}
        <div className="login-header">
          <button onClick={handleBack} className="back-button">
            <FaArrowLeft />
            Volver
          </button>
        </div>

        {/* Logo y título */}
        <div className="login-logo">
          <div className="logo-icon">
            <FaCar />
          </div>
          <h1>LUBRICENTROS</h1>
        </div>

        {/* Título principal */}
        <div className="login-title-section">
          <div className="user-icon">
            <FaUserShield />
          </div>
          <h2>Crear Cuenta</h2>
          <p>Completa tus datos para activar tu cuenta</p>
        </div>

        {/* Formulario */}
        <form className="login-form" onSubmit={handleSignup}>
          {/* Email (readonly si viene de la invitación) */}
          <div className="form-field">
            <label>Correo electrónico</label>
            <div className="input-wrapper">
              <FaEnvelope className="input-icon" />
              <input
                type="email"
                placeholder="ejemplo@lubricentros.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                disabled={loading || !!invitationEmail}
              />
            </div>
          </div>

          {/* Nombre */}
          <div className="form-field">
            <label>Nombre</label>
            <div className="input-wrapper">
              <FaUser className="input-icon" />
              <input
                type="text"
                placeholder="Tu nombre"
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                required
                disabled={loading}
              />
            </div>
          </div>

          {/* Apellido */}
          <div className="form-field">
            <label>Apellido</label>
            <div className="input-wrapper">
              <FaUser className="input-icon" />
              <input
                type="text"
                placeholder="Tu apellido"
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                required
                disabled={loading}
              />
            </div>
          </div>
          
          {/* Password */}
          <div className="form-field">
            <label>Contraseña</label>
            <div className="input-wrapper">
              <FaLock className="input-icon" />
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Ingresa tu contraseña"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                disabled={loading}
                style={{ paddingRight: '2.5rem' }}
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="password-toggle"
              >
                {showPassword ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
              </button>
            </div>
            {passwordError && (
              <div className="error-message">
                <FaLock />
                {passwordError}
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div className="form-field">
            <label>Confirmar Contraseña</label>
            <div className="input-wrapper">
              <FaLock className="input-icon" />
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirma tu contraseña"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                required
                disabled={loading}
                style={{ paddingRight: '2.5rem' }}
              />
              <button
                type="button"
                onClick={toggleConfirmPasswordVisibility}
                className="password-toggle"
              >
                {showConfirmPassword ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
              </button>
            </div>
            {confirmPasswordError && (
              <div className="error-message">
                <FaLock />
                {confirmPasswordError}
              </div>
            )}
          </div>
          
          {/* Error message */}
          {errorMsg && (
            <div className="error-message">
              <FaLock />
              {errorMsg}
            </div>
          )}
          
          {/* Submit button */}
          <button type="submit" className="submit-button" disabled={loading}>
            {loading ? (
              <>
                <div className="loading-spinner"></div>
                Creando cuenta...
              </>
            ) : (
              <>
                <FaSignInAlt />
                Crear Cuenta
              </>
            )}
          </button>
        </form>

        {/* Footer */}
        <div className="login-footer">
          <p>Sistema de gestión de lubricentros</p>
        </div>
      </div>
    </div>
  )
}